import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ProgressRing from '../components/ui/ProgressRing';
import Sparkline from '../components/ui/Sparkline';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { GameService } from '../services/gameService';
import { GAME_CONFIG } from '../config/constants';
import { formatNumber } from '../utils/formatters';

const {
  FiTarget, FiTrendingUp, FiZap, FiSun, FiLayers, FiCamera
} = FiIcons;

const GARDEN_THEMES = [
  {
    id: 'ethereal',
    name: "Ethereal Sanctuary",
    images: [
      "https://cdn.midjourney.com/4d59fc89-16e5-4aa5-acfe-e1cad4beb6f9/0_0.webp",
      "https://cdn.midjourney.com/4d59fc89-16e5-4aa5-acfe-e1cad4beb6f9/0_1.webp",
      "https://cdn.midjourney.com/4d59fc89-16e5-4aa5-acfe-e1cad4beb6f9/0_2.webp",
      "https://cdn.midjourney.com/4d59fc89-16e5-4aa5-acfe-e1cad4beb6f9/0_3.webp"
    ]
  },
  {
    id: 'ancient',
    name: "Ancient Grove",
    images: [
      "https://cdn.midjourney.com/72922e89-cb0c-4c44-b659-4e2771ae03ca/0_0.webp",
      "https://cdn.midjourney.com/72922e89-cb0c-4c44-b659-4e2771ae03ca/0_1.webp",
      "https://cdn.midjourney.com/72922e89-cb0c-4c44-b659-4e2771ae03ca/0_2.webp",
      "https://cdn.midjourney.com/72922e89-cb0c-4c44-b659-4e2771ae03ca/0_3.webp"
    ]
  },
  {
    id: 'watercolor',
    name: "Watercolor Dreams",
    images: [
      "https://cdn.midjourney.com/8c4a6613-253b-4f26-8037-9f699f15c293/0_0.webp",
      "https://cdn.midjourney.com/8c4a6613-253b-4f26-8037-9f699f15c293/0_1.webp",
      "https://cdn.midjourney.com/8c4a6613-253b-4f26-8037-9f699f15c293/0_2.webp",
      "https://cdn.midjourney.com/8c4a6613-253b-4f26-8037-9f699f15c293/0_3.webp"
    ]
  },
  {
    id: 'golden',
    name: "Golden Hour",
    images: [
      "https://cdn.midjourney.com/02d6d1bc-0f05-4f06-bd82-c38d49ca5e12/0_0.webp",
      "https://cdn.midjourney.com/02d6d1bc-0f05-4f06-bd82-c38d49ca5e12/0_1.webp",
      "https://cdn.midjourney.com/02d6d1bc-0f05-4f06-bd82-c38d49ca5e12/0_2.webp",
      "https://cdn.midjourney.com/02d6d1bc-0f05-4f06-bd82-c38d49ca5e12/0_3.webp"
    ]
  },
  {
    id: 'mystic',
    name: "Mystic Twilight",
    images: [
      "https://cdn.midjourney.com/e91b8885-0683-49d9-8d9c-bf274541ba3e/0_0.webp",
      "https://cdn.midjourney.com/e91b8885-0683-49d9-8d9c-bf274541ba3e/0_1.webp",
      "https://cdn.midjourney.com/e91b8885-0683-49d9-8d9c-bf274541ba3e/0_2.webp",
      "https://cdn.midjourney.com/e91b8885-0683-49d9-8d9c-bf274541ba3e/0_3.webp"
    ]
  }
];

export default function Garden() {
  const { state, actions } = useGame();
  const [isHarvesting, setIsHarvesting]         = useState(false);
  const [currentThemeIndex, setCurrentThemeIndex]         = useState(0);
  const [currentVariationIndex, setCurrentVariationIndex] = useState(0);
  const [clickEffects, setClickEffects]         = useState([]);
  const harvestIntervalRef = useRef();
  const containerRef       = useRef(null);

  const sps              = GameService.calculateSPS(state.planters, state.gardeners, state.autoLevel, state.sps, state.compost);
  const clickYield       = GameService.calculateClickYield(state.effLevel, state.compost);
  const prestigeReq      = GameService.calculatePrestigeRequirement(state.prestigeLevel);
  const prestigeProgress = (state.seeds / prestigeReq) * 100;
  const canPrestige      = state.seeds >= prestigeReq;
  const currentTheme     = GARDEN_THEMES[currentThemeIndex];
  const currentImage     = currentTheme.images[currentVariationIndex];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentVariationIndex((p) => (p + 1) % currentTheme.images.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [currentThemeIndex, currentVariationIndex, currentTheme.images.length]);

  const addClickEffect = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    const x = e.clientX - (rect?.left || 0);
    const y = e.clientY - (rect?.top || 0);
    const id = Date.now();
    setClickEffects((p) => [...p, { id, x, y, val: `+${formatNumber(clickYield)}` }]);
    setTimeout(() => setClickEffects((p) => p.filter((ef) => ef.id !== id)), 1000);
  };

  const performHarvest = (e) => {
    actions.harvest();
    if (e && containerRef.current) addClickEffect(e);
  };

  const startHarvest = (e) => {
    setIsHarvesting(true);
    performHarvest(e);
    harvestIntervalRef.current = setInterval(() => actions.harvest(), 80);
  };

  const stopHarvest = () => {
    setIsHarvesting(false);
    clearInterval(harvestIntervalRef.current);
  };

  useEffect(() => () => clearInterval(harvestIntervalRef.current), []);

  return (
    <div className="space-y-6">
      <div className="text-center lg:text-left flex flex-col lg:flex-row justify-between items-end gap-4">
        <div>
          <h1 className="text-3xl font-cormorant font-bold text-charcoal mb-1">Your Sanctuary</h1>
          <p className="text-charcoal/60 font-dm-sans">Tend to your garden and watch your efforts bloom.</p>
        </div>
        <div className="bg-white/50 backdrop-blur-sm px-4 py-2 rounded-xl border border-sage/10 text-sm text-charcoal/80 font-dm-sans flex gap-4">
          <div><span className="font-bold">{GARDEN_THEMES.length}</span> Themes</div>
          <div><span className="font-bold">{GARDEN_THEMES.length * 4}</span> Variations</div>
        </div>
      </div>

      {/* Immersive Garden Area */}
      <div
        ref={containerRef}
        className="relative w-full h-[500px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white/50 group select-none bg-charcoal"
      >
        <AnimatePresence>
          <motion.div
            key={`${currentTheme.id}-${currentVariationIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            <div className="absolute inset-0 bg-black/10 z-10" />
            <img
              src={currentImage}
              alt={currentTheme.name}
              className="w-full h-full object-cover transition-transform duration-[20s] ease-linear scale-110 group-hover:scale-100"
            />
            <div className="absolute top-6 left-6 z-20">
              <div className="bg-black/30 backdrop-blur-md px-4 py-2 rounded-xl border border-white/10 text-white">
                <div className="text-xs opacity-80 uppercase tracking-wider">Current Yield</div>
                <div className="text-xl font-bold font-dm-sans">{formatNumber(clickYield, 1)} / click</div>
              </div>
            </div>
            <div className="absolute top-6 right-6 z-20 flex flex-col items-end gap-2">
              <div className="bg-black/30 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 text-white/90 text-sm font-dm-sans flex items-center gap-2">
                <SafeIcon icon={FiSun} className="w-4 h-4" />
                {currentTheme.name}
              </div>
              <div className="text-white/60 text-xs bg-black/20 px-2 py-1 rounded-md backdrop-blur-sm">
                <SafeIcon icon={FiCamera} className="w-3 h-3 inline mr-1" />
                View {currentVariationIndex + 1}/{currentTheme.images.length}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Harvest Button */}
        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onMouseDown={startHarvest}
            onMouseUp={stopHarvest}
            onMouseLeave={stopHarvest}
            onTouchStart={startHarvest}
            onTouchEnd={stopHarvest}
            className={`group relative w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.3)] ${isHarvesting ? 'bg-white/20 scale-95' : 'bg-white/10 hover:bg-white/20'} backdrop-blur-sm border border-white/30`}
          >
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/40 animate-spin-slow opacity-50 group-hover:opacity-80" />
            <div className="flex flex-col items-center text-white drop-shadow-lg">
              <SafeIcon icon={FiTarget} className="w-8 h-8 md:w-10 md:h-10 mb-2" />
              <span className="font-dm-sans font-bold tracking-widest text-sm md:text-base">HARVEST</span>
            </div>
          </motion.button>
          <p className="mt-6 text-white/80 font-dm-sans text-sm drop-shadow-md bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm">
            Hold to harvest continuously
          </p>
        </div>

        {/* Click Effects */}
        <AnimatePresence>
          {clickEffects.map((effect) => (
            <motion.div
              key={effect.id}
              initial={{ opacity: 1, y: effect.y - 20, x: effect.x }}
              animate={{ opacity: 0, y: effect.y - 100 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="absolute pointer-events-none z-30 text-2xl font-bold text-white drop-shadow-[0_2px_2px_rgba(0,0,0,0.5)] font-dm-sans"
              style={{ left: 0, top: 0 }}
            >
              {effect.val}
            </motion.div>
          ))}
        </AnimatePresence>

        {/* View Controls */}
        <div className="absolute bottom-6 right-6 z-20 flex gap-2">
          <Button
            variant="secondary" size="sm"
            onClick={() => setCurrentVariationIndex((p) => (p + 1) % currentTheme.images.length)}
            className="bg-white/20 hover:bg-white/30 text-white border-white/20 backdrop-blur-md"
            icon={FiCamera}
          >
            Angle
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={() => { setCurrentThemeIndex((p) => (p + 1) % GARDEN_THEMES.length); setCurrentVariationIndex(0); }}
            className="bg-white/20 hover:bg-white/30 text-white border-white/20 backdrop-blur-md"
            icon={FiLayers}
          >
            Theme
          </Button>
        </div>
      </div>

      {/* Stats & Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-1 border-goldenrod/20 bg-gradient-to-b from-white to-beige/30">
          <div className="text-center mb-4">
            <h2 className="text-xl font-cormorant font-bold text-charcoal mb-1">Prestige Progress</h2>
            <p className="text-xs text-charcoal/60 font-dm-sans uppercase tracking-wide">
              {canPrestige ? 'Ascension Ready' : 'Growth Cycle'}
            </p>
          </div>
          <div className="flex justify-center mb-6">
            <ProgressRing
              progress={Math.min(prestigeProgress, 100)}
              size={140} strokeWidth={10}
              color={canPrestige ? '#E2A93B' : '#9BB898'}
            >
              <div className="text-center">
                <div className="text-2xl font-bold text-charcoal">{Math.round(prestigeProgress)}%</div>
                {canPrestige && <div className="text-xs font-bold text-goldenrod mt-1">READY</div>}
              </div>
            </ProgressRing>
          </div>
          <div className="text-center">
            {canPrestige ? (
              <Button variant="warning" onClick={actions.prestige} className="w-full shadow-lg shadow-goldenrod/20 animate-pulse-soft">
                Prestige Now (+{GAME_CONFIG.prestige.compostBonus}x)
              </Button>
            ) : (
              <div className="text-sm text-charcoal/60 bg-sage/10 rounded-lg p-2">
                Need <span className="font-bold text-charcoal">{formatNumber(prestigeReq - state.seeds)}</span> more seeds
              </div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="p-5 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute right-0 top-0 p-4 opacity-5">
              <SafeIcon icon={FiTrendingUp} className="w-24 h-24 text-skyblue" />
            </div>
            <div>
              <div className="text-sm text-charcoal/60 font-dm-sans mb-1">Seeds Per Second</div>
              <div className="text-3xl font-bold text-charcoal mb-2">{formatNumber(sps, 1)}</div>
              <div className="flex items-center gap-2 text-xs">
                <span className="bg-skyblue/10 text-skyblue px-2 py-1 rounded-md font-bold">
                  {state.compost.toFixed(2)}x Multiplier
                </span>
              </div>
            </div>
            <div className="mt-6 h-16">
              <Sparkline data={state.history} width={200} height={60} color="#6DAEDB" />
            </div>
          </Card>

          <div className="grid grid-rows-2 gap-4">
            <Card className="p-4 flex items-center justify-between bg-gradient-to-r from-sage/10 to-transparent">
              <div>
                <div className="text-sm text-charcoal/60 font-dm-sans">Click Power</div>
                <div className="text-2xl font-bold text-charcoal">{formatNumber(clickYield, 1)}</div>
              </div>
              <div className="w-10 h-10 bg-sage rounded-full flex items-center justify-center text-white shadow-lg shadow-sage/30">
                <SafeIcon icon={FiTarget} className="w-5 h-5" />
              </div>
            </Card>
            <Card className="p-4 flex items-center justify-between bg-gradient-to-r from-goldenrod/10 to-transparent">
              <div>
                <div className="text-sm text-charcoal/60 font-dm-sans">Compost Level</div>
                <div className="text-2xl font-bold text-charcoal">{state.compost.toFixed(2)}x</div>
              </div>
              <div className="w-10 h-10 bg-goldenrod rounded-full flex items-center justify-center text-white shadow-lg shadow-goldenrod/30">
                <SafeIcon icon={FiZap} className="w-5 h-5" />
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}